// === WT5 recon === — страница сверок поставщиков (ТЗ §14).
// Wizard: выбор поставщика → настройка формата → загрузка Excel → предпросмотр
// → запуск фоновой сверки → результаты по категориям → разбор исключений.
// Supplier internals доступны только техролям и финансам — backend резает 403.

import { useEffect, useMemo, useState } from 'react';
import { parties, recon } from '../api/endpoints';
import type {
    Party, ReconciliationRow, ReconciliationRowResult, ReconciliationRun,
    ReconPreview, SupplierFormat, SupplierIdentifierKind,
} from '../api/types';
import {
    Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney,
    PageHeader, shortId, StatusBadge, Tabs,
} from '../components/ui';
import { useAsync } from '../hooks';

const RESULT_LABELS: Record<string, string> = {
    matched: 'Совпало',
    missing_ours: 'Нет у нас',
    missing_supplier: 'Нет в файле',
    amount_mismatch: 'Расхождение суммы',
    ambiguous: 'Неоднозначно',
    row_error: 'Ошибка строки',
    pending: 'Ожидает',
};

const RESULT_FILTERS = ['', 'matched', 'missing_ours', 'missing_supplier',
    'amount_mismatch', 'ambiguous', 'row_error'];

const IDENTIFIER_KINDS: Array<{ key: SupplierIdentifierKind; title: string }> = [
    { key: 'ds', title: 'ДС' },
    { key: 'pi', title: 'ПИ' },
    { key: 'other', title: 'Другое' },
];

function resultLabel(value?: string | null): string {
    return RESULT_LABELS[value ?? ''] ?? value ?? '—';
}

// ---------------------------------------------------------------------------
// Настройка формата поставщика
// ---------------------------------------------------------------------------

function FormatEditor({ supplierId, formats, onSaved }: {
    supplierId: string;
    formats: SupplierFormat[];
    onSaved: () => void;
}) {
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [identifierKind, setIdentifierKind] = useState<SupplierIdentifierKind>('ds');
    const [sheetName, setSheetName] = useState('');
    const [sheetIndex, setSheetIndex] = useState('1');
    const [headerRow, setHeaderRow] = useState('1');
    const [dateFormat, setDateFormat] = useState('%d.%m.%Y');
    const [defaultCurrency, setDefaultCurrency] = useState('RUB');
    const [mappingJson, setMappingJson] = useState(
        '{\n  "A": "number",\n  "B": "date",\n  "C": "amount",\n'
        + '  "D": "currency",\n  "E": "description",\n  "F": "laboratory",\n'
        + '  "G": "supplier"\n}');
    const [rulesJson, setRulesJson] = useState(
        '{\n  "strip_prefixes": ["ДС", "№"],\n  "uppercase": true\n}');

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            let mapping: Record<string, string>;
            let rules: Record<string, unknown>;
            try {
                mapping = JSON.parse(mappingJson);
            } catch {
                throw new Error('column_mapping: некорректный JSON.');
            }
            try {
                rules = JSON.parse(rulesJson);
            } catch {
                throw new Error('normalization_rules: некорректный JSON.');
            }
            await recon.createFormat({
                supplier: supplierId,
                document_kind: 'recon',
                identifier_kind: identifierKind,
                sheet_name: sheetName,
                sheet_index: parseInt(sheetIndex, 10) || 1,
                header_row: parseInt(headerRow, 10) || 1,
                date_format: dateFormat,
                default_currency: defaultCurrency,
                column_mapping: mapping,
                normalization_rules: rules,
            });
            onSaved();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <Card title="Форматы файлов поставщика">
            <ErrorBox error={err} />
            {formats.length ? (
                <DataTable<SupplierFormat>
                    columns={[
                        { key: 'version', title: 'v', width: '40px' },
                        { key: 'document_kind', title: 'Вид' },
                        { key: 'identifier_kind', title: 'Идентификатор',
                          render: (f) => IDENTIFIER_KINDS.find(k => k.key === f.identifier_kind)?.title ?? f.identifier_kind },
                        { key: 'sheet', title: 'Лист',
                          render: (f) => f.sheet_name || `№${f.sheet_index}` },
                        { key: 'mapping', title: 'Колонки',
                          render: (f) => Object.keys(f.column_mapping || {}).length },
                        { key: 'active', title: 'Активен',
                          render: (f) => f.active ? 'да' : 'нет' },
                    ]}
                    rows={formats}
                />
            ) : <Empty text="Формат не настроен — создайте ниже." />}

            <details style={{ marginTop: 12 }}>
                <summary>Новый формат</summary>
                <div className="form-grid" style={{ marginTop: 8 }}>
                    <Field label="Идентификатор">
                        <select className="input" value={identifierKind}
                                onChange={(e) => setIdentifierKind(e.target.value as SupplierIdentifierKind)}>
                            {IDENTIFIER_KINDS.map((k) => <option key={k.key} value={k.key}>{k.title}</option>)}
                        </select>
                    </Field>
                    <Field label="Лист (имя)">
                        <input className="input" value={sheetName}
                               placeholder="пусто — по номеру"
                               onChange={(e) => setSheetName(e.target.value)} />
                    </Field>
                    <Field label="Лист (№)">
                        <input className="input" value={sheetIndex}
                               onChange={(e) => setSheetIndex(e.target.value)} />
                    </Field>
                    <Field label="Строка заголовка">
                        <input className="input" value={headerRow}
                               onChange={(e) => setHeaderRow(e.target.value)} />
                    </Field>
                    <Field label="Формат даты">
                        <input className="input" value={dateFormat}
                               onChange={(e) => setDateFormat(e.target.value)} />
                    </Field>
                    <Field label="Валюта по умолчанию">
                        <input className="input" value={defaultCurrency}
                               onChange={(e) => setDefaultCurrency(e.target.value)} />
                    </Field>
                </div>
                <Field label="column_mapping (JSON: колонка → поле)">
                    <textarea className="input" rows={7} value={mappingJson}
                              onChange={(e) => setMappingJson(e.target.value)} />
                </Field>
                <Field label="normalization_rules (JSON)">
                    <textarea className="input" rows={4} value={rulesJson}
                              onChange={(e) => setRulesJson(e.target.value)} />
                </Field>
                <div className="form-actions">
                    <button className="btn primary" disabled={busy}
                            onClick={() => void submit()}>
                        Сохранить формат
                    </button>
                </div>
            </details>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Загрузка файла + предпросмотр
// ---------------------------------------------------------------------------

function UploadCard({ supplierId, formatId, onRun }: {
    supplierId: string;
    formatId?: string;
    onRun: (run: ReconciliationRun) => void;
}) {
    const [file, setFile] = useState<File | null>(null);
    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!file || !periodFrom || !periodTo) return;
        setBusy(true); setErr(null);
        try {
            const run = await recon.upload(file, {
                supplier_id: supplierId,
                period_from: periodFrom,
                period_to: periodTo,
                format_id: formatId,
            });
            onRun(run);
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <Card title="Новая сверка">
            <ErrorBox error={err} />
            <div className="form-grid">
                <Field label="Файл поставщика (.xlsx)">
                    <input className="input" type="file" accept=".xlsx"
                           onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                </Field>
                <Field label="Период с">
                    <input className="input" type="date" value={periodFrom}
                           onChange={(e) => setPeriodFrom(e.target.value)} />
                </Field>
                <Field label="по">
                    <input className="input" type="date" value={periodTo}
                           onChange={(e) => setPeriodTo(e.target.value)} />
                </Field>
            </div>
            <div className="form-actions" style={{ marginTop: 8 }}>
                <button className="btn primary" disabled={busy || !file || !periodFrom || !periodTo}
                        onClick={() => void submit()}>
                    Загрузить и показать предпросмотр
                </button>
            </div>
        </Card>
    );
}

function PreviewCard({ run, onStarted }: {
    run: ReconciliationRun;
    onStarted: () => void;
}) {
    const [preview, setPreview] = useState<ReconPreview | null>(null);
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setErr(null);
        try { setPreview(await recon.preview(run.id)); }
        catch (x) { setErr(x); }
    };
    useEffect(() => { void load(); }, [run.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async () => {
        setBusy(true); setErr(null);
        try {
            await recon.start(run.id, run.revision);
            onStarted();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <Card title="Предпросмотр файла">
            <ErrorBox error={err} />
            {preview?.quarantine ? (
                <div className="alert alert-warn">
                    <strong>Файл на карантине</strong>
                    <span>Антивирусная проверка ещё не завершена (scan_status: {preview.file?.scan_status}). Обновите через несколько секунд.</span>
                    <button className="btn small" onClick={() => void load()}>Обновить</button>
                </div>
            ) : null}
            {preview && !preview.quarantine ? (
                <>
                    {preview.warnings.map((w, i) => (
                        <div className="alert alert-warn" key={i}>
                            <strong>Повторная загрузка.</strong>
                            <span>{w.message}</span>
                        </div>
                    ))}
                    {preview.duplicate_file_runs.length ? (
                        <div className="alert alert-warn">
                            <span>Тот же файл уже был в прогоне(ах): {preview.duplicate_file_runs.map(shortId).join(', ')}</span>
                        </div>
                    ) : null}
                    {preview.format_errors.map((e, i) => (
                        <div className="alert alert-error" key={i}><strong>Формат.</strong><span>{e}</span></div>
                    ))}
                    <div className="muted" style={{ marginBottom: 8 }}>
                        Колонки: {preview.columns.map((c) => `${c.column}→${c.field}`).join(', ') || '—'}
                        {' · '}Строк данных: {preview.total_rows}
                    </div>
                    <DataTable<ReconPreview['rows'][number]>
                        columns={[
                            { key: 'row_index', title: '№', width: '50px' },
                            { key: 'identifier_raw', title: 'Номер (исх.)' },
                            { key: 'identifier_normalized', title: 'Номер (норм.)' },
                            { key: 'date', title: 'Дата',
                              render: (r) => fmtDate(r.parsed.date) },
                            { key: 'amount', title: 'Сумма',
                              render: (r) => fmtMoney(r.parsed.amount, r.parsed.currency) },
                            { key: 'error', title: 'Ошибка',
                              render: (r) => r.error ? <span className="badge bad">{r.error}</span> : '—' },
                        ]}
                        rows={preview.rows}
                    />
                    <div className="form-actions" style={{ marginTop: 8 }}>
                        <button className="btn primary"
                                disabled={busy || !!preview.format_errors.length || !preview.total_rows}
                                onClick={() => void start()}>
                            Запустить сверку
                        </button>
                    </div>
                </>
            ) : null}
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Результаты и разбор исключений
// ---------------------------------------------------------------------------

function ResolveForm({ runId, row, onDone }: {
    runId: string; row: ReconciliationRow; onDone: () => void;
}) {
    const [decision, setDecision] = useState<'accept' | 'reject' | 'clarify'>('accept');
    const [comment, setComment] = useState('');
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            await recon.resolveRow(runId, { row_id: row.id, decision, comment });
            onDone();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <div className="form-actions">
            <select className="input" value={decision}
                    onChange={(e) => setDecision(e.target.value as typeof decision)}>
                <option value="accept">Принять расхождение</option>
                <option value="reject">Отклонить строку файла</option>
                <option value="clarify">На уточнение</option>
            </select>
            <input className="input" style={{ width: 260 }} placeholder="Комментарий"
                   value={comment} onChange={(e) => setComment(e.target.value)} />
            <button className="btn small primary" disabled={busy || !comment.trim()}
                    onClick={() => void submit()}>
                Зафиксировать
            </button>
            <ErrorBox error={err} />
        </div>
    );
}

function RunDetail({ run, onRefresh }: { run: ReconciliationRun; onRefresh: () => void }) {
    const [resultFilter, setResultFilter] = useState('');
    const rowsState = useAsync(
        () => recon.rows(run.id, resultFilter ? { result: resultFilter } : {}),
        [run.id, resultFilter, run.finished_at]);
    const [resolving, setResolving] = useState<string | null>(null);
    const stats = run.stats || {};

    const columns: Column<ReconciliationRow>[] = [
        { key: 'row_index', title: '№', width: '50px',
          render: (r) => r.row_index || '—' },
        { key: 'side', title: 'Сторона', width: '70px',
          render: (r) => r.side === 'ours' ? 'наша' : 'файл' },
        { key: 'identifier', title: 'Номер',
          render: (r) => (
            <div>
                <div>{r.identifier_raw || '—'}</div>
                {r.identifier_normalized !== r.identifier_raw && (
                    <div className="muted">→ {r.identifier_normalized || '—'}</div>
                )}
            </div>
          ) },
        { key: 'amount', title: 'Сумма',
          render: (r) => fmtMoney(
              r.parsed_row?.amount as string | number | undefined,
              (r.parsed_row?.currency as string) || '') },
        { key: 'result', title: 'Результат',
          render: (r) => <StatusBadge value={r.result} /> },
        { key: 'detail', title: 'Детали',
          render: (r) => {
              const d = r.detail || {};
              const bits: string[] = [];
              if (d.reason) bits.push(String(d.reason));
              if (d.error) bits.push(String(d.error));
              const ours = d.ours as Record<string, string> | undefined;
              const file = d.file as Record<string, string> | undefined;
              if (ours && file) bits.push(`наш: ${ours.amount} ${ours.currency} / файл: ${file.amount} ${file.currency}`);
              if (r.file_supplier) bits.push(`пост.: ${r.file_supplier}`);
              if (r.laboratory) bits.push(`лаб.: ${r.laboratory}`);
              return <span className="muted">{bits.join(' · ') || '—'}</span>;
          } },
        { key: 'resolution', title: 'Решение',
          render: (r) => {
              const res = r.exception_resolution as Record<string, unknown> | undefined;
              if (res && res.decision) {
                  return <span>{String(res.decision)} · {String(res.comment ?? '')}</span>;
              }
              if (r.result !== 'matched' && r.result !== 'pending' && run.status === 'done') {
                  return resolving === r.id
                      ? <ResolveForm runId={run.id} row={r}
                                     onDone={() => { setResolving(null); rowsState.reload(); }} />
                      : <button className="btn small" onClick={() => setResolving(r.id)}>Разобрать</button>;
              }
              return '—';
          } },
    ];

    return (
        <Card title={`Прогон ${shortId(run.id)}`}
              actions={<StatusBadge value={run.status} />}>
            <div className="kv-grid" style={{ marginBottom: 8 }}>
                <div className="kv"><span className="kv-key">Период</span>
                    <span className="kv-value">{fmtDate(run.period_from)} — {fmtDate(run.period_to)}</span></div>
                <div className="kv"><span className="kv-key">Строк</span>
                    <span className="kv-value">{stats.rows ?? '—'}</span></div>
                <div className="kv"><span className="kv-key">Исключений</span>
                    <span className="kv-value">{stats.unresolved ?? '—'}</span></div>
                <div className="kv"><span className="kv-key">Ошибка</span>
                    <span className="kv-value">{run.error || '—'}</span></div>
            </div>
            {stats.by_result ? (
                <div className="form-actions" style={{ marginBottom: 8 }}>
                    {RESULT_FILTERS.map((f) => (
                        <button key={f || 'all'}
                                className={`btn small ${resultFilter === f ? 'primary' : ''}`}
                                onClick={() => setResultFilter(f)}>
                            {f ? `${resultLabel(f)} (${stats.by_result?.[f] ?? 0})` : 'Все'}
                        </button>
                    ))}
                </div>
            ) : null}
            {run.warnings?.map((w, i) => (
                <div className="alert alert-warn" key={i}>
                    <span>{w.message}</span>
                </div>
            ))}
            <ErrorBox error={rowsState.error} />
            <DataTable<ReconciliationRow> columns={columns} rows={rowsState.data} />
            <div className="form-actions" style={{ marginTop: 8 }}>
                <button className="btn small" onClick={onRefresh}>Обновить статус</button>
            </div>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Страница
// ---------------------------------------------------------------------------

export function ReconciliationPage() {
    const [supplierId, setSupplierId] = useState('');
    const [runId, setRunId] = useState('');
    const partiesState = useAsync(() => parties.list({}), []);
    const formatsState = useAsync(
        () => supplierId ? recon.formats({ supplier: supplierId }) : Promise.resolve([]),
        [supplierId]);
    const runsState = useAsync(
        () => supplierId ? recon.runs({ supplier: supplierId }) : Promise.resolve([]),
        [supplierId]);
    const [tab, setTab] = useState('runs');

    const formats = formatsState.data ?? [];
    const defaultFormat = useMemo(
        () => [...formats].filter((f) => f.active)
            .sort((a, b) => b.version - a.version)[0],
        [formats]);

    const run = (runsState.data ?? []).find((r) => r.id === runId) ?? null;

    // Поллинг активного прогона.
    useEffect(() => {
        if (!run || !['queued', 'running'].includes(run.status)) return;
        const t = setInterval(() => runsState.reload(), 3000);
        return () => clearInterval(t);
    }, [run?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <PageHeader title="Сверки поставщиков" />
            <ErrorBox error={partiesState.error} />

            <Card title="Поставщик">
                <Field label="Поставщик">
                    <select className="input" value={supplierId}
                            onChange={(e) => { setSupplierId(e.target.value); setRunId(''); }}>
                        <option value="">— выберите —</option>
                        {(partiesState.data ?? []).map((p: Party) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </Field>
            </Card>

            {supplierId ? (
                <>
                    <Tabs active={tab} onChange={setTab} tabs={[
                        { key: 'runs', title: 'Прогоны' },
                        { key: 'format', title: 'Формат' },
                        { key: 'new', title: 'Новая сверка' },
                    ]} />
                    <ErrorBox error={formatsState.error ?? runsState.error} />

                    {tab === 'format' ? (
                        <FormatEditor supplierId={supplierId} formats={formats}
                                      onSaved={() => formatsState.reload()} />
                    ) : null}

                    {tab === 'new' ? (
                        <>
                            <UploadCard supplierId={supplierId}
                                        formatId={defaultFormat?.id}
                                        onRun={(r) => { setRunId(r.id); setTab('runs'); runsState.reload(); }} />
                            {run && run.status === 'draft' ? (
                                <PreviewCard run={run} onStarted={() => runsState.reload()} />
                            ) : null}
                        </>
                    ) : null}

                    {tab === 'runs' ? (
                        <>
                            <Card title="Прогоны сверок"
                                  actions={<button className="btn small" onClick={() => runsState.reload()}>Обновить</button>}>
                                <DataTable<ReconciliationRun>
                                    columns={[
                                        { key: 'id', title: 'ID',
                                          render: (r) => shortId(r.id) },
                                        { key: 'period', title: 'Период',
                                          render: (r) => `${fmtDate(r.period_from)} — ${fmtDate(r.period_to)}` },
                                        { key: 'status', title: 'Статус',
                                          render: (r) => <StatusBadge value={r.status} /> },
                                        { key: 'rows', title: 'Строк',
                                          render: (r) => r.stats?.rows ?? '—' },
                                        { key: 'unresolved', title: 'Исключения',
                                          render: (r) => r.stats?.unresolved ?? '—' },
                                        { key: 'created_at', title: 'Создан',
                                          render: (r) => fmtDate(r.created_at) },
                                    ]}
                                    rows={runsState.data}
                                    onRowClick={(r) => setRunId(r.id === runId ? '' : r.id)}
                                    empty="Прогонов пока нет — создайте сверку на вкладке «Новая сверка»." />
                            </Card>
                            {run ? (
                                <>
                                    {run.status === 'draft' ? (
                                        <PreviewCard run={run} onStarted={() => runsState.reload()} />
                                    ) : (
                                        <RunDetail run={run} onRefresh={() => runsState.reload()} />
                                    )}
                                </>
                            ) : null}
                        </>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
